import type {
  MultipleProductCard,
  SingularProductCard,
  VariantType
} from "@shared/graphql.types";
import { useState, useMemo, useEffect } from "react";
import { isMultipleProductCard } from "packages/core-shopping/shared/isMultipleProductCard";

import type { ProductCardTrackingPayload } from "../types";

import { useProductCardHandlers } from "./useProductCardHandler";
import { useProductImpression } from "./useProductImpression";
import { useInView } from "react-intersection-observer";
import { CtaButtonAction } from "../HorizontalProductCardWrapper";

interface ProductCardOptions {
  location: string;
  productClickedTrackingPayload?: ProductCardTrackingPayload;
  addToBasketOptions?: {
    afterAdd?: (variant: VariantType) => void;
    beforeAdd?: () => void;
  };
  enableShadeSelector?: boolean;
  ctaButtonAction?: CtaButtonAction;
  productCardClick?: () => void;
  shouldTrackProductImpression?: boolean;
}

export const useProductCard = (
  product: SingularProductCard | MultipleProductCard,
  options: ProductCardOptions
) => {
  const [isShadeSelectorOpen, setIsShadeSelectorOpen] = useState(false);
  const [activeShade, setActiveShade] = useState(0);
  const { onProductView: impressionOnProductView } = useProductImpression();

  const productHandlers = useProductCardHandlers(product, {
    location: options.location,
    activeShade,
    productClickedTrackingPayload: options.productClickedTrackingPayload || {},
    addToBasketOptions: options.addToBasketOptions || {},
    ctaButtonAction: options.ctaButtonAction || CtaButtonAction.ADD_TO_BASKET
  });

  const openShadeSelector = () => setIsShadeSelectorOpen(true);

  const handleShadeSelectorClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();

    if (options.enableShadeSelector && isMultipleProductCard(product)) {
      openShadeSelector();
    }
  };

  const onCardClick = () => {
    productHandlers.onCardClick();
    options.productCardClick?.();
  };

  const shouldTrackImpression = options.shouldTrackProductImpression !== false;
  const onProductView = useMemo(() => {
    return shouldTrackImpression
      ? impressionOnProductView
      : () => {
          // No-op function when tracking is disabled
        };
  }, [shouldTrackImpression, impressionOnProductView]);

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true
  });

  useEffect(() => {
    const variant = product.variants[activeShade];
    if (inView && onProductView && variant.sku) {
      onProductView(variant.sku);
    }
  }, [inView, onProductView, activeShade, product]);

  // Determine the CTA button behavior based on action configuration
  const ctaAction = options.ctaButtonAction ?? CtaButtonAction.ADD_TO_BASKET;
  const shouldUseShadeSelector =
    ctaAction === CtaButtonAction.SELECT_SHADE &&
    isMultipleProductCard(product);

  // Choose the appropriate handler for the CTA button
  // For "selectShade" mode on multi-variant products, open the shade selector
  // For all other cases (including "selectShade" on single-variant), use add to basket
  const ctaButtonHandler = shouldUseShadeSelector
    ? openShadeSelector
    : productHandlers.onButtonClick;

  const handlers = {
    addToBasket: productHandlers.onButtonClick,
    ctaButton: ctaButtonHandler,
    toggleWishlist: productHandlers.onWishlistToggle,
    trackProductClick: productHandlers.onCardClick,
    openShadeSelector,
    closeShadeSelector: () => setIsShadeSelectorOpen(false),
    selectShade: (index: number) => setActiveShade(index),
    ...(options.enableShadeSelector &&
      isMultipleProductCard(product) && {
        onShadeSelectorClick: handleShadeSelectorClick
      }),
    onCardClick
  };

  const shouldRenderShadeSelector =
    options.enableShadeSelector && isMultipleProductCard(product);

  return {
    productCardData: productHandlers.productCardData,
    activeVariant: productHandlers.activeVariant,
    isShadeSelectorOpen,
    shouldRenderShadeSelector,
    handlers,
    inViewRef
  };
};
